import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { ActiveImportJob } from '@domain/connection/models/ImportJob';

const POLL_INTERVAL_MS = 5000;

export const ACTIVE_IMPORT_JOBS_QUERY_KEY = ['active-import-jobs'] as const;

/** Turns a SyncTarget slug (e.g. 'orders-new') into a readable label. */
export const formatImportTarget = (target: string) =>
  target.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Polls active/waiting/delayed import jobs. When `connectionId` is set, only
 * jobs for that connection are returned.
 */
export const useActiveImportJobs = (connectionId?: string) => {
  const query = useQuery({
    queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY,
    queryFn: async () => {
      const response = await container.importJobsRepository.fetchActiveJobs();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });

  const jobs = useMemo(() => {
    const all = query.data ?? [];
    if (!connectionId) return all;
    return all.filter((job: ActiveImportJob) => job.connectionId === connectionId);
  }, [connectionId, query.data]);

  return {
    jobs,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    refetch: query.refetch,
  };
};
